/**
 * @module ReasoningFailedBanner
 * @purpose Red alert strip shown above a case's evidence view when the latest
 *   reasoning run failed -- states the failure plainly and offers a retry action,
 *   without hiding a prior completed run's results that may still be displayed
 *   underneath it.
 * @depends react-i18next, i18n/utils/message-keys (resolveMessageKey)
 * @dependedOnBy CanvasReasoningPanel
 *
 * @rules
 * - Performance: pure display -- no data fetching
 * - State: none
 * - Errors: role="alert" for screen readers. The raw `errorMessage.message` string
 *   from the backend is NEVER rendered -- only the whitelisted reason key resolved
 *   from `errorMessage.code` drives the copy shown to the analyst.
 */

import { useTranslation } from 'react-i18next'

import { resolveMessageKey } from '../../../../i18n/utils/message-keys'
import { formatRunTimestamp } from './CanvasReasoningPanelState'

export interface ReasoningFailedBannerErrorMessage {
  code: string
  message: string
}

interface ReasoningFailedBannerProps {
  onRetry: () => void
  failedAt: string | null
  errorMessage: ReasoningFailedBannerErrorMessage | null
}

const REASON_KEY_PREFIX = 'reasoning.failedBanner.reason'
const KNOWN_REASON_TOKENS = ['stage_failed', 'internal_error'] as const
const STAGE_FAILED_CODE_PATTERN = /^stage[2-6]_failed$/
const STAGE_FAILED_TOKEN = 'stage_failed'

const BANNER_STYLE: React.CSSProperties = {
  background: 'rgba(220, 38, 38, 0.08)',
  borderBottom: '1px solid rgba(220, 38, 38, 0.25)',
  color: 'rgb(153, 27, 27)',
}

const DARK_BANNER_OVERRIDE = 'dark:text-red-300 dark:border-red-700/40 dark:bg-red-900/10'

/**
 * @behaviour Normalises every `stageN_failed` run-row code (Stage-2 through
 *   Stage-6) onto the single `stage_failed` token before delegating to
 *   `resolveMessageKey`, so the one whitelist-not-concatenation guarantee in
 *   message-keys.ts covers every code-to-key mapping here -- an unrecognised or
 *   absent code always resolves to the generic reason, never to an arbitrary
 *   key path built from the code itself.
 * @errors n/a -- never throws, always returns a key string
 */
export function resolveReasonKey(code: string | undefined): string {
  const token = code !== undefined && STAGE_FAILED_CODE_PATTERN.test(code) ? STAGE_FAILED_TOKEN : code
  return resolveMessageKey(token, KNOWN_REASON_TOKENS, REASON_KEY_PREFIX)
}

/**
 * Red alert strip for a failed reasoning run, with a reason clause and retry action.
 *
 * @behaviour Renders the `messageAt` form (failure timestamp interpolated) when
 *            `failedAt` is present, else the plain `message` form. The reason
 *            clause is a separate translated sentence resolved via
 *            `resolveReasonKey` -- the raw `errorMessage.message` string is never
 *            read or rendered. Retry calls `onRetry`; the caller decides what
 *            request that repeats.
 * @errors n/a -- purely presentational
 */
export function ReasoningFailedBanner({
  onRetry,
  failedAt,
  errorMessage,
}: ReasoningFailedBannerProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const reasonKey = resolveReasonKey(errorMessage?.code)

  return (
    <div
      role="alert"
      className={`flex items-start justify-between px-3 py-2 text-xs ${DARK_BANNER_OVERRIDE}`}
      style={BANNER_STYLE}
    >
      <span style={{ fontFamily: 'var(--font-body)' }}>
        {failedAt
          ? t('reasoning.failedBanner.messageAt', { when: formatRunTimestamp(failedAt) })
          : t('reasoning.failedBanner.message')}
        {' '}
        {t(reasonKey as never)}
      </span>
      <button
        type="button"
        onClick={onRetry}
        className="ml-3 shrink-0 uppercase tracking-wide underline hover:no-underline"
        style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}
      >
        {t('reasoning.failedBanner.retry')}
      </button>
    </div>
  )
}
